import React, { useState, useEffect } from 'react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { usePremium } from '../../../hooks/usePremium';
import { AlertCircle, CheckCircle, Users, Loader2 } from 'lucide-react';
import { JoinLeaderboardResponse } from '../../../types/api';

interface JoinLeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (groupId: string, groupName: string) => void;
  joinLeaderboard: (inviteCode: string) => Promise<JoinLeaderboardResponse>;
  isProcessing: boolean;
  userGroupCount: number;
  prefilledCode?: string;
}

export const JoinLeaderboardModal: React.FC<JoinLeaderboardModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  joinLeaderboard,
  isProcessing,
  userGroupCount,
  prefilledCode
}) => {
  const { isPremium, loading: premiumLoading } = usePremium();

  const [inviteCode, setInviteCode] = useState(prefilledCode?.toUpperCase() || '');
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<{ group_name: string } | null>(null);

  // Update invite code when prefilledCode prop changes
  useEffect(() => {
    if (prefilledCode) {
      setInviteCode(prefilledCode.toUpperCase());
    }
  }, [prefilledCode]);

  // Check if user can join more leaderboards (only when not loading)
  const canJoin = !premiumLoading && (isPremium || userGroupCount < 1);
  const remainingSlots = isPremium ? '∞' : Math.max(0, 1 - userGroupCount).toString();

  const handleSubmit = async () => {
    if (isProcessing) {
      return;
    }

    setError(null);

    // Validation
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    if (inviteCode.trim().length !== 8) {
      setError('Invite code must be 8 characters');
      return;
    }

    if (!canJoin) {
      setError('Free users are limited to 1 private leaderboard. Upgrade to Premium for unlimited access.');
      return;
    }

    // Join leaderboard
    const result = await joinLeaderboard(inviteCode.trim());

    if (result.success && result.group_name) {
      setSuccessData({
        group_name: result.group_name
      });
      onSuccess?.(result.group_id || '', result.group_name);
    } else {
      setError(result.error || 'Failed to join leaderboard');
    }
  };

  const handleClose = () => {
    setInviteCode('');
    setError(null);
    setSuccessData(null);
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Convert to uppercase and limit to 8 characters
    const value = e.target.value.toUpperCase().slice(0, 8);
    setInviteCode(value);
    setError(null); // Clear error on input change
  };

  // Success view
  if (successData) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Successfully Joined!"
        className="max-w-md"
      >
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-700 dark:text-gray-300">
              You've successfully joined
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mt-2">
              {successData.group_name}
            </p>
          </div>

          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            You can now compete with other members on the private leaderboard!
          </p>

          <Button onClick={handleClose} className="w-full">
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  // Join form view
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Join Private Leaderboard"
      description="Enter the invite code shared by the leaderboard admin"
      className="max-w-md"
    >
      <div className="p-6 space-y-6">
        {/* Premium Loading State */}
        {premiumLoading ? (
          <div className="p-4 bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 rounded-lg flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-gray-600 dark:text-gray-400" />
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Loading subscription status...
            </div>
          </div>
        ) : (
          /* Access Gate Warning */
          !canJoin && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <p className="font-medium">Free Tier Limit Reached</p>
                <p className="mt-1">
                  Free users are limited to 1 private leaderboard. You currently have {userGroupCount} leaderboard{userGroupCount === 1 ? '' : 's'}.
                  Upgrade to Premium for unlimited access.
                </p>
              </div>
            </div>
          )
        )}

        {/* Invite Code Input */}
        <div className="space-y-3">
          <label htmlFor="invite-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Invite Code
          </label>
          <input
            id="invite-code"
            type="text"
            value={inviteCode}
            onChange={handleInputChange}
            placeholder="ABCD1234"
            maxLength={8}
            className="w-full px-4 py-3 text-center text-2xl font-mono tracking-wider border-2 border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white uppercase"
            disabled={premiumLoading || !canJoin}
            autoFocus
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {inviteCode.length}/8 characters
          </p>
        </div>

        {/* Help Text */}
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Ask the leaderboard admin for the 8-character invite code or use the shareable link they provided.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1"
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="flex-1"
            disabled={isProcessing || premiumLoading || !canJoin || inviteCode.length !== 8}
          >
            {isProcessing ? 'Joining...' : 'Join Leaderboard'}
          </Button>
        </div>

        {/* Remaining Slots Info */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <Users className="inline w-4 h-4 mr-1" />
          {premiumLoading ? (
            <span>Loading subscription status...</span>
          ) : isPremium ? (
            <span className="text-blue-600 dark:text-blue-400 font-medium">
              Premium: Unlimited leaderboards
            </span>
          ) : (
            <span>
              Remaining slots: <span className="font-medium">{remainingSlots}/1</span>
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
};
